// src/EmployeeForm.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { api } from './lib/apiFetch';

const EMP_PATH  = '/employees';
const PERF_PATH = '/performance';

/* ---------- helpers ---------- */
const norm = (s) => String(s ?? '').trim();
const lc   = (s) => norm(s).toLowerCase();

function displayName(emp = {}) {
  const pf = norm(emp.preferredFirst);
  const pl = norm(emp.preferredLast);
  const f  = norm(emp.firstName);
  const l  = norm(emp.lastName);
  if (pl || pf) return [pl || l, pf || f].filter(Boolean).join(', ');
  return [l, f].filter(Boolean).join(', ');
}

// Accepts ISO, yyyy-mm-dd, m/d/yyyy, or Date-ish -> returns yyyy-mm-dd for <input type="date">
function toDateInputValue(x) {
  if (!x) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(x)) return x;
  if (/^\d{4}-\d{2}-\d{2}T/.test(x)) {
    const d = new Date(x); if (Number.isNaN(d)) return '';
    const yyyy = d.getUTCFullYear();
    const mm   = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd   = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  const mdy = x.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (mdy) { const [, mm, dd, yyyy] = mdy; return `${yyyy}-${mm}-${dd}`; }
  const d = new Date(x); if (Number.isNaN(d)) return '';
  const yyyy = d.getUTCFullYear();
  const mm   = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd   = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const fmtPct = (v) => Number.isFinite(v) ? `${v.toFixed(1)} %` : '—';
const fmtPts = (v) => Number.isFinite(v) ? `${v.toFixed(0)} pts` : '—';
const belowOn  = (v) => Number(v) < 95;
const belowSaf = (v) => Number(v) < 850;

const normStatus = (s) => lc(s).replace(/\s+/g, '_').toUpperCase();
const prettyStatus = (s) => {
  const n = normStatus(s);
  if (!n) return '—';
  if (n === 'NOT_STARTED') return 'Not Started';
  if (n === 'IN_PROGRESS') return 'In Progress';
  if (n === 'COMPLETE') return 'Complete';
  return n.toLowerCase().replace(/(^|_)./g, (m) => m.slice(-1).toUpperCase()).replace(/_/g, ' ');
};
const statusPillStyle = (s) => {
  const n = normStatus(s);
  let bg = '#444', color = '#fff', border = '#333';
  if (n === 'NOT_STARTED') { bg = '#3b0d12'; border = '#5a1a21'; }
  else if (n === 'IN_PROGRESS') { bg = '#4a3a04'; border = '#6b5706'; }
  else if (n === 'COMPLETE') { bg = '#0f3b1a'; border = '#1f6a35'; }
  return {
    display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 700,
    background: bg, color, border: `1px solid ${border}`, minWidth: 88, textAlign: 'center',
  };
};

/* ---------- component ---------- */
export default function EmployeeForm({ employee, onCancelEdit, onSuccessfulSubmit }) {
  /* Auth */
  const [isAuthed, setIsAuthed] = useState(false);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await getCurrentUser();
        if (mounted) setIsAuthed(true);
      } catch {
        try {
          const s = await fetchAuthSession();
          const authed = !!s?.tokens?.accessToken || !!s?.tokens?.idToken;
          if (mounted) setIsAuthed(authed);
        } catch {
          if (mounted) setIsAuthed(false);
        }
      }
    })();
    const unsub = Hub.listen('auth', ({ payload }) => {
      const evt = payload?.event;
      if (evt === 'signedIn') setIsAuthed(true);
      if (evt === 'signedOut' || evt === 'tokenRefresh_failure') setIsAuthed(false);
    });
    return () => { mounted = false; unsub(); };
  }, []);

  /* Seed from props (includes all fields so ESLint is happy) */
  const employeeSeed = useMemo(() => ({
    id: employee?.id ?? undefined,        // legacy employee row id (ISO)
    empId: employee?.empId ?? '',         // NEW: canonical UUID if present on the employee record
    firstName: employee?.firstName ?? '',
    lastName: employee?.lastName ?? '',
    preferredFirst: employee?.preferredFirst ?? '',
    preferredLast: employee?.preferredLast ?? '',
    email: employee?.email ?? '',
    phone: employee?.phone ?? '',
    empPosition: employee?.empPosition ?? 'driver',
    status: employee?.status ?? 'active',
    startDate: employee?.startDate ?? '',
    _new: !!employee?._new,
  }), [
    employee?.id,
    employee?.empId,   // watch for UUID if backend starts returning it
    employee?.firstName,
    employee?.lastName,
    employee?.preferredFirst,
    employee?.preferredLast,
    employee?.email,
    employee?.phone,
    employee?.empPosition,
    employee?.status,
    employee?.startDate,
    employee?._new,
  ]);

  /* Local form state + edit mode */
  const [isEditing, setIsEditing] = useState(!!employee?._new);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const [form, setForm] = useState(() => {
    const { _new, ...seed } = employeeSeed;
    return seed;
  });

  // Only resync when the *id* changes (prevents losing typed edits)
  const prevIdRef = useRef(employee?.id);
  useEffect(() => {
    if (employee?.id !== prevIdRef.current) {
      setIsEditing(!!employeeSeed._new);
      const { _new, ...seed } = employeeSeed;
      setForm(seed);
      prevIdRef.current = employee?.id;
    }
  }, [employee?.id, employeeSeed, employee?._new]);

  const titleName = displayName(form);

  /* Performance panel — fetch by canonical empId if present */
  const [perfRow, setPerfRow] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);
  const [perfErr, setPerfErr] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function fetchPerfForEmp(empId) {
      setPerfErr('');
      setPerfLoading(true);
      try {
        // Backend is expected to look up by canonical empId UUID
        const row = await api.get(`${PERF_PATH}?empId=${encodeURIComponent(empId)}`);
        if (!cancelled) setPerfRow(row || null);
      } catch (e) {
        const msg = String(e?.message || e || '');
        if (!cancelled) {
          // Treat 404 / not found as "no data", not an error banner
          if (/404/.test(msg) || /not\s*found/i.test(msg)) {
            setPerfRow(null);
            setPerfErr('');
          } else {
            setPerfRow(null);
            setPerfErr(msg);
          }
        }
      } finally {
        if (!cancelled) setPerfLoading(false);
      }
    }

    // Only fetch if we have a canonical empId
    if (form.empId) {
      fetchPerfForEmp(form.empId);
    } else {
      setPerfRow(null);
      setPerfErr('');
      setPerfLoading(false);
    }

    return () => { cancelled = true; };
  }, [form.empId]);

  /* Handlers */
  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const resetForm = () => {
    const { _new, ...seed } = employeeSeed;
    setForm(seed);
  };

  const cancelOrClose = () => {
    if (isEditing && !employeeSeed._new) {
      resetForm();
      setIsEditing(false);
      setErr('');
      return;
    }
    onCancelEdit?.();
  };

  const save = useCallback(async () => {
    if (!isAuthed) {
      setErr('Please sign in to save employee changes.');
      return;
    }
    setSubmitting(true);
    setErr('');
    try {
      const body = { ...form, startDate: toDateInputValue(form.startDate) || '' };

      // Single POST handles create or update (by presence of body.id)
      const resp = await api.post(EMP_PATH, body);
      if (resp?.ok === false) throw new Error(resp?.message || 'Save failed.');

      onSuccessfulSubmit?.();
      setIsEditing(false);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setSubmitting(false);
    }
  }, [form, isAuthed, onSuccessfulSubmit]);

  /* ---------- UI ---------- */
  return (
    <div className="employee-form-shell" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
      {/* LEFT: details panel */}
      <div className="container listDiv" style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h3 style={{ marginTop: 0 }}>{employeeSeed._new ? 'Add Employee' : 'Employee Details'}</h3>
          {isEditing && <span style={{ fontSize: 12, fontWeight: 700, color: '#2563eb' }}>Edit mode</span>}
        </div>
        <h4 style={{ marginTop: 4, color: '#bbb' }}>{titleName || '(unnamed)'}</h4>

        {!isAuthed && <div style={{ marginBottom: 8, color: '#9ca3af' }}>You’re not signed in.</div>}
        {isEditing && !isAuthed && (
          <div style={{ margin: '8px 0', color: '#d97706' }}>
            You can edit locally, but you must sign in to save changes.
          </div>
        )}
        {err && <div style={{ color: '#C80036', margin: '8px 0' }}>{err}</div>}

        <form onSubmit={(e) => { e.preventDefault(); void save(); }}>
          <div className="input-row">
            <div className="input-group">
              <label>First Name</label>
              <input name="firstName" value={form.firstName} onChange={onChange} disabled={!isEditing || submitting} />
            </div>
            <div className="input-group">
              <label>Last Name</label>
              <input name="lastName" value={form.lastName} onChange={onChange} disabled={!isEditing || submitting} />
            </div>
          </div>

          <div className="input-row">
            <div className="input-group">
              <label>Preferred First</label>
              <input name="preferredFirst" value={form.preferredFirst} onChange={onChange} disabled={!isEditing || submitting} />
            </div>
            <div className="input-group">
              <label>Preferred Last</label>
              <input name="preferredLast" value={form.preferredLast} onChange={onChange} disabled={!isEditing || submitting} />
            </div>
          </div>

          <div className="input-row">
            <div className="input-group">
              <label>Email</label>
              <input type="email" name="email" value={form.email} onChange={onChange} disabled={!isEditing || submitting} />
            </div>
            <div className="input-group">
              <label>Phone</label>
              <input name="phone" value={form.phone} onChange={onChange} disabled={!isEditing || submitting} />
            </div>
          </div>

          <div className="input-row">
            <div className="input-group">
              <label>Position</label>
              <input
                name="empPosition"
                value={form.empPosition}
                onChange={onChange}
                disabled={!isEditing || submitting}
                list="positions"
                placeholder="driver, dispatcher, …"
              />
              <datalist id="positions">
                <option value="driver" />
                <option value="dispatcher" />
                <option value="mechanic" />
                <option value="manager" />
              </datalist>
            </div>
            <div className="input-group">
              <label>Status</label>
              <select name="status" value={form.status} onChange={onChange} disabled={!isEditing || submitting}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="input-row">
            <div className="input-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={toDateInputValue(form.startDate)}
                onChange={(e) => setForm((s) => ({ ...s, startDate: e.target.value }))}
                disabled={!isEditing || submitting}
              />
            </div>
          </div>

          <div className="button-container" style={{ marginTop: 12 }}>
            {!isEditing ? (
              <>
                <button type="button" onClick={() => setIsEditing(true)} disabled={submitting}>Edit</button>
                <button type="button" onClick={cancelOrClose}>Close</button>
              </>
            ) : (
              <>
                <button type="submit" disabled={submitting || !isAuthed}>{submitting ? 'Saving…' : 'Save'}</button>
                <button type="button" onClick={cancelOrClose} disabled={submitting}>Cancel</button>
              </>
            )}
          </div>
        </form>
      </div>

      {/* RIGHT: performance snapshot */}
      <div className="container listDiv" style={{ minWidth: 0 }}>
        <h3 style={{ marginTop: 0 }}>Performance</h3>

        {/* Guidance when no canonical empId is present */}
        {!form.empId && (
          <div style={{ color: '#9ca3af' }}>
            No performance record linked yet — this employee does not have a canonical ID.
            {form.id ? ' Save or update this employee so the system can attach a canonical empId.' : ' Save this employee first.'}
          </div>
        )}

        {form.empId && (
          <>
            {perfLoading ? (
              <div>Loading performance…</div>
            ) : perfErr ? (
              <div style={{ color: '#C80036' }}>{perfErr}</div>
            ) : !perfRow ? (
              <div>No performance data found for “{titleName || '—'}”.</div>
            ) : (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: '#888' }}>Record:</div>
                  <div style={{ fontWeight: 600 }}>
                    {displayName({
                      firstName: perfRow.canonicalFirst,
                      lastName: perfRow.canonicalLast,
                    }) || '—'}
                  </div>
                </div>

                <div className="perf-card" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="perf-block">
                    <div style={{ fontSize: 12, color: '#aaa' }}>On-time %</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: belowOn(Number(perfRow?.onTimePct)) ? '#C80036' : 'white' }}>
                      {fmtPct(Number(perfRow?.onTimePct))}
                    </div>
                  </div>
                  <div className="perf-block">
                    <div style={{ fontSize: 12, color: '#aaa' }}>Safety</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: belowSaf(Number(perfRow?.safetyScore)) ? '#C80036' : 'white' }}>
                      {fmtPts(Number(perfRow?.safetyScore))}
                    </div>
                  </div>
                  <div className="perf-block" style={{ gridColumn: '1 / span 2' }}>
                    <div style={{ fontSize: 12, color: '#aaa' }}>Training</div>
                    <div><span style={statusPillStyle(perfRow?.trainingStatus)}>{prettyStatus(perfRow?.trainingStatus)}</span></div>
                  </div>
                </div>

                {(perfRow?.weekStart || perfRow?.weekEnd) && (
                  <div style={{ marginTop: 10, fontSize: 12, color: '#999' }}>
                    {perfRow?.weekStart ? `Week start: ${new Date(perfRow.weekStart).toLocaleDateString()}` : null}
                    {perfRow?.weekStart && perfRow?.weekEnd ? ' • ' : null}
                    {perfRow?.weekEnd ? `Week end: ${new Date(perfRow.weekEnd).toLocaleDateString()}` : null}
                  </div>
                )}

                <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
                  <div>• On-time below 95% may affect overtime/bonuses.</div>
                  <div>• Safety below 850 may remove bonus hours and overtime eligibility.</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
